import {
  faXmark,
  faVial,
  faWavePulse,
  faChartSimple,
} from "@fortawesome/pro-regular-svg-icons";
import { createColumnHelper } from "@tanstack/react-table";
import React, { useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { twJoin, twMerge } from "tailwind-merge";

import { DatasetPurpose } from "src/api/types";
import { Icon } from "src/base-components/Icon";
import {
  TableComp as Table,
  Header as TableHeader,
  Cell as TableCell,
} from "src/base-components/Table";
import { useDatasets } from "src/datasets/api/queries";
import { EmptyState } from "src/design-system/EmptyState";
import { useModal } from "src/design-system/Modal";
import { usePollRunsInProgress } from "src/jobs/api/queries";
import { JobRun, JobRunStatusType } from "src/jobs/types";
import { LeftPaneOptions } from "src/router/SearchParams";
import {
  useAuthoringContext,
  useFlowContext,
} from "src/router/routerContextHooks";
import { getSimulationVersionUrl } from "src/router/urls";
import { CreateDatasetFromFailedRowsModal } from "src/simulations/CreateDatasetFromFailedRowsModal";
import { CreateSimulationModal } from "src/simulations/CreateSimulationModal";
import { SimulationOptionsDropdown } from "src/simulations/SimulationOptionsDropdown";
import { SimulationStatusIndicator } from "src/simulations/SimulationStatusIndicator";
import {
  useCreateFailedSimulationDatasetJob,
  useCreateSimulationDownloadDatasetJob,
  useCreateSimulationLinkDatasetJob,
  useMarkSimulationAsDeleted,
} from "src/simulations/api/mutations";
import { CreateSimulationButton } from "src/simulations/common/CreateSimulationButton";
import {
  isSimulationComplete,
  isSimulationSuccessful,
} from "src/simulations/simulationUtils";
import { useSimulationJob } from "src/simulations/useSimulationJob";
import { useSimulationRuns } from "src/simulations/useSimulationRun";

type Props = {
  onRequestClose: () => void;
  onAssembleDataset: (purpose: DatasetPurpose) => void;
};

export const SimulationsSidePane: React.FC<Props> = ({
  onRequestClose,
  onAssembleDataset,
}) => {
  const {
    isOpen: isCreateSimulationModalOpen,
    closeModal: closeCreateSimulationModal,
    openModal: openCreateSimulationModal,
  } = useModal();
  const {
    isOpen: isCreateFailedDatasetModalOpen,
    closeModal: closeCreateFailedDatasetModal,
    openModal: openCreateFailedDatasetModal,
    data: createFailedDatasetModalData,
  } = useModal<{ simulationRunId: string }>();
  const { workspace, flow } = useFlowContext();
  const { version } = useAuthoringContext();
  const navigate = useNavigate();

  const { simulationJob } = useSimulationJob(flow.id, workspace.base_url ?? "");
  const {
    simulationRuns: simulationJobRuns,
    isLoading: simulationRunsLoading,
  } = useSimulationRuns(flow.id, version.id);

  usePollRunsInProgress(workspace.base_url!, simulationJobRuns ?? []);

  const { data: datasets, isLoading: datasetsLoading } = useDatasets({
    flowId: flow.id,
    baseUrl: workspace.base_url,
  });
  // Memoed because it is used for column generation in a child
  const datasetNamesBySourceId = useMemo(() => {
    return datasets?.reduce(
      (acc, dataset) => {
        if (dataset.simulation_dataset_detail?.job_source_id) {
          acc[dataset.simulation_dataset_detail?.job_source_id] = dataset.name;
        }
        return acc;
      },
      {} as Record<string, string>,
    );
  }, [datasets]);

  const isLoading =
    simulationRunsLoading || datasetsLoading || simulationJobRuns === undefined;

  // Should be moved to hasSimulation(version) or useHasSimulation() from useHasSimulation once simulation meta patching is implemented
  const hasSimulations =
    simulationJobRuns !== undefined && simulationJobRuns.length > 0;

  const handleCreateSimulation = () => {
    openCreateSimulationModal();
  };

  const { mutate: markSimulationAsDeletedMutation } =
    useMarkSimulationAsDeleted(workspace.base_url!, simulationJob?.id || "");
  // Memoising these callbacks because they are passed to table columns
  const handleDeleteSimulation = useCallback(
    (simulationJobRunId: string) => {
      markSimulationAsDeletedMutation(simulationJobRunId);
    },
    [markSimulationAsDeletedMutation],
  );

  const {
    mutateAsync: convertFailedToTestDataset,
    isPending: isDatasetCreationPending,
  } = useCreateFailedSimulationDatasetJob(workspace.base_url!, flow.id);
  const handleConvertFailedToTestDataset = useCallback(
    (simulationJobRunId: string, name: string) => {
      return convertFailedToTestDataset({ simulationJobRunId, name });
    },
    [convertFailedToTestDataset],
  );

  const { mutate: createDownloadJob } = useCreateSimulationDownloadDatasetJob(
    workspace.base_url!,
    flow.id,
  );

  const { mutateAsync: createDownloadJobForCopy } =
    useCreateSimulationLinkDatasetJob(workspace.base_url!, flow.id);

  const handleDownloadCsv = useCallback(
    (simulationJobRunId: string) => {
      createDownloadJob({
        simulationJobRunId,
        format: "csv",
      });
    },
    [createDownloadJob],
  );

  const handleDownloadNdjson = useCallback(
    (simulationJobRunId: string) => {
      createDownloadJob({
        simulationJobRunId,
        format: "ndjson",
      });
    },
    [createDownloadJob],
  );

  const handleCopyCsv = useCallback(
    (simulationJobRunId: string) => {
      createDownloadJobForCopy({
        simulationJobRunId,
        format: "csv",
      });
    },
    [createDownloadJobForCopy],
  );

  const handleCopyNdjson = useCallback(
    (simulationJobRunId: string) => {
      createDownloadJobForCopy({
        simulationJobRunId,
        format: "ndjson",
      });
    },
    [createDownloadJobForCopy],
  );

  const handleSimulationClick = useCallback(
    (simulationJobRun: JobRun) => {
      const simulationUrl = getSimulationVersionUrl(
        workspace.organization_id,
        workspace.id,
        flow.id,
        version.id,
        simulationJobRun.id,
      );
      navigate(simulationUrl);
    },
    [workspace.organization_id, workspace.id, flow.id, version.id, navigate],
  );

  const handleAnalyticsClick = useCallback(
    (simulationJobRun: JobRun) => {
      const simulationUrl = getSimulationVersionUrl(
        workspace.organization_id,
        workspace.id,
        flow.id,
        version.id,
        simulationJobRun.id,
        { leftPane: LeftPaneOptions.Analytics },
      );
      navigate(simulationUrl);
    },
    [workspace.organization_id, workspace.id, flow.id, version.id, navigate],
  );

  return (
    <div className="flex h-full w-128 flex-col bg-white py-6">
      <Header
        hasSimulations={hasSimulations}
        isLoading={isLoading}
        onCreateSimulation={handleCreateSimulation}
        onRequestClose={onRequestClose}
      />
      <div className="decideScrollbar mt-6 grow border-t border-gray-100">
        <SimulationsTable
          datasetNamesBySourceId={datasetNamesBySourceId ?? {}}
          handleCreateSimulation={handleCreateSimulation}
          hasSimulations={hasSimulations}
          isDatasetCreationPending={isDatasetCreationPending}
          isLoading={isLoading}
          simulationJobRuns={simulationJobRuns ?? []}
          onAnalyticsClick={handleAnalyticsClick}
          onCopyCsvUrl={handleCopyCsv}
          onCopyNdjsonUrl={handleCopyNdjson}
          onCreateFailedDataset={openCreateFailedDatasetModal}
          onDeleteSimulation={handleDeleteSimulation}
          onDownloadCsv={handleDownloadCsv}
          onDownloadNdjson={handleDownloadNdjson}
          onSimulationClick={handleSimulationClick}
        />
      </div>
      <CreateSimulationModal
        open={isCreateSimulationModalOpen}
        onAssembleDataset={onAssembleDataset}
        onClose={closeCreateSimulationModal}
      />
      <CreateDatasetFromFailedRowsModal
        closeModal={closeCreateFailedDatasetModal}
        isDatasetCreationPending={isDatasetCreationPending}
        isOpen={isCreateFailedDatasetModalOpen}
        simulationJobRunId={createFailedDatasetModalData?.simulationRunId}
        onCreateFailedDataset={handleConvertFailedToTestDataset}
      />
    </div>
  );
};

const helper = createColumnHelper<JobRun>();

const columnTextClassName = "text-gray-800 font-inter-medium-13px";
const datasetColumnSize = 220;

const getColumns = (
  datasetNamesBySourceId: Record<string, string>,
  isDatasetCreationPending: boolean,
  onSimulationClick: (simulationJobRun: JobRun) => void,
  onAnalyticsClick: (simulationJobRun: JobRun) => void,
  onDeleteSimulation: (simulationJobRunId: string) => void,
  onCreateFailedDataset: ({
    simulationRunId,
  }: {
    simulationRunId: string;
  }) => void,
  onDownloadCsv: (simulationJobRunId: string) => void,
  onDownloadNdjson: (simulationJobRunId: string) => void,
  onCopyCsvUrl: (simulationJobRunId: string) => void,
  onCopyNdjsonUrl: (simulationJobRunId: string) => void,
) => [
  helper.display({
    id: "dataset",
    header: (info) => (
      <TableHeader dataLoc="simulated-dataset-header" info={info}>
        <span className={twJoin("px-4", columnTextClassName)}>
          Simulated dataset
        </span>
      </TableHeader>
    ),
    cell: (info) => (
      <TableCell className="mx-3.5" info={info}>
        <button
          className="flex h-full w-full items-center"
          data-loc={info.row.original.id}
          disabled={!isSimulationSuccessful(info.row.original)}
          onClick={() => onSimulationClick(info.row.original)}
        >
          <div className="flex items-center">
            <Icon color="text-gray-400" icon={faWavePulse} size="xs" />
            <div className="min-w-0 flex-1 truncate">
              <span
                className={twMerge(
                  "font-medium text-gray-700 font-inter-medium-12px",
                  isSimulationSuccessful(info.row.original) &&
                    "group-hover:text-indigo-600",
                )}
              >
                {datasetNamesBySourceId[info.row.original.source_id] ||
                  "Unknown Dataset"}
              </span>
            </div>
          </div>
        </button>
      </TableCell>
    ),
    size: datasetColumnSize,
    minSize: datasetColumnSize,
    maxSize: datasetColumnSize,
  }),
  helper.display({
    id: "results",
    header: (info) => (
      <TableHeader dataLoc="results-header" info={info}>
        <span className={columnTextClassName}>Results</span>
      </TableHeader>
    ),
    cell: (info) => (
      <TableCell info={info}>
        <div className="flex h-full w-full items-center justify-between gap-x-6">
          <div className="flex-1">
            <SimulationStatusIndicator {...info.row.original} />
          </div>
          <div
            className={twMerge(
              "invisible flex items-center gap-x-1",
              isSimulationComplete(info.row.original) && "group-hover:visible",
            )}
            data-loc="simulation-row-buttons-container"
          >
            <button
              className="flex items-center justify-center"
              data-loc="simulation-analytics-button"
              disabled={!isSimulationComplete(info.row.original)}
              onClick={() => {
                onAnalyticsClick(info.row.original);
              }}
            >
              <Icon
                color="text-gray-500 hover:text-gray-700"
                icon={faChartSimple}
                size="xs"
              />
            </button>
            <SimulationOptionsDropdown
              hasFailedRows={info.row.original.failed_rows_count > 0}
              hasSuccessfulRows={info.row.original.success_rows_count > 0}
              isCompleted={
                info.row.original.status.type === JobRunStatusType.Completed
              }
              isDatasetCreationPending={isDatasetCreationPending}
              onCopyCsvUrl={() => onCopyCsvUrl(info.row.original.id)}
              onCopyNdjsonUrl={() => onCopyNdjsonUrl(info.row.original.id)}
              onCreateFailedDataset={() =>
                onCreateFailedDataset({
                  simulationRunId: info.row.original.id,
                })
              }
              onDelete={() => onDeleteSimulation(info.row.original.id)}
              onDownloadCsv={() => onDownloadCsv(info.row.original.id)}
              onDownloadNdjson={() => onDownloadNdjson(info.row.original.id)}
            />
          </div>
        </div>
      </TableCell>
    ),
  }),
];

export const SimulationsTable: React.FC<{
  simulationJobRuns: JobRun[];
  onSimulationClick: (simulationJobRun: JobRun) => void;
  onAnalyticsClick: (simulationJobRun: JobRun) => void;
  onDeleteSimulation: (simulationJobRunId: string) => void;
  onCreateFailedDataset: ({
    simulationRunId,
  }: {
    simulationRunId: string;
  }) => void;
  onDownloadCsv: (simulationJobRunId: string) => void;
  onDownloadNdjson: (simulationJobRunId: string) => void;
  onCopyCsvUrl: (simulationJobRunId: string) => void;
  onCopyNdjsonUrl: (simulationJobRunId: string) => void;
  datasetNamesBySourceId: Record<string, string>;
  isDatasetCreationPending: boolean;
  isLoading: boolean;
  hasSimulations: boolean;
  handleCreateSimulation: () => void;
}> = ({
  simulationJobRuns,
  onSimulationClick,
  onAnalyticsClick,
  datasetNamesBySourceId,
  isDatasetCreationPending,
  isLoading,
  handleCreateSimulation,
  onDeleteSimulation,
  onCreateFailedDataset,
  onDownloadCsv,
  onDownloadNdjson,
  onCopyCsvUrl,
  onCopyNdjsonUrl,
  hasSimulations,
}) => {
  const columns = useMemo(
    () =>
      getColumns(
        datasetNamesBySourceId,
        isDatasetCreationPending,
        onSimulationClick,
        onAnalyticsClick,
        onDeleteSimulation,
        onCreateFailedDataset,
        onDownloadCsv,
        onDownloadNdjson,
        onCopyCsvUrl,
        onCopyNdjsonUrl,
      ),
    [
      datasetNamesBySourceId,
      isDatasetCreationPending,
      onSimulationClick,
      onAnalyticsClick,
      onDeleteSimulation,
      onCreateFailedDataset,
      onDownloadCsv,
      onDownloadNdjson,
      onCopyCsvUrl,
      onCopyNdjsonUrl,
    ],
  );
  const showTable = isLoading || hasSimulations;
  if (showTable) {
    return (
      <div className="bg-white">
        <Table
          columns={columns}
          data={simulationJobRuns}
          dataLoc="simulations-table"
          frameClassName="h-auto border-gray-100 border-b"
          isLoading={isLoading}
          noScroll={true}
          rowClassName="group hover:bg-gray-50"
        />
      </div>
    );
  } else {
    return (
      <EmptyState
        action={<CreateSimulationButton onClick={handleCreateSimulation} />}
        description="Create your first simulation"
        headline="No simulations available"
        icon={faVial}
      />
    );
  }
};

const Header = ({
  hasSimulations,
  onRequestClose,
  onCreateSimulation,
  isLoading,
}: {
  hasSimulations: boolean;
  onCreateSimulation: () => void;
  onRequestClose: () => void;
  isLoading: boolean;
}) => {
  return (
    <>
      <div>
        <div className="mb-2 flex items-start justify-around px-6">
          <div className="grow">
            <div className="mt-0.5 text-gray-800 font-inter-semibold-16px">
              Simulations
            </div>
          </div>
          <div className="mt-1 flex flex-row items-center gap-x-2">
            {hasSimulations && (
              <CreateSimulationButton size="sm" onClick={onCreateSimulation} />
            )}
            <Icon
              color="text-gray-500 hover:text-gray-700"
              dataLoc="close-simulations-side-pane"
              icon={faXmark}
              size="xs"
              onClick={onRequestClose}
            />
          </div>
        </div>
      </div>
      <span className="px-6 text-gray-500 font-inter-normal-12px">
        Simulate changes to understand their impact
      </span>
      {!hasSimulations && !isLoading && (
        <div className="mb-1 mt-4 px-6">
          <div className="h-px w-full rounded-full bg-gray-100"></div>
        </div>
      )}
    </>
  );
};
